import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, Atom, Calculator, FlaskConical, Globe,
  Languages, Music, Palette, Cpu, Leaf, Brain, Heart,
  Plus, Moon, Sun, LogOut, Flame, Sparkles
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { listSubjects } from "@/lib/api";
import { SUBJECT_COLORS } from "@/lib/subjectMeta";

const ICONS = { BookOpen, Atom, Calculator, FlaskConical, Globe, Languages, Music, Palette, Cpu, Leaf, Brain, Heart };

export default function Layout({ children }) {
  const { user, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [subjects, setSubjects] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    listSubjects().then((s) => { if (mounted) setSubjects(s); }).catch(() => {});
    return () => { mounted = false; };
  }, [location.pathname]);

  const goLogout = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950 text-black dark:text-white">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-72 nb-border bg-white dark:bg-zinc-900 p-5 flex flex-col gap-4 transform transition-transform lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center gap-2">
          <div className="nb-border rounded-xl bg-[#FFE066] w-10 h-10 flex items-center justify-center nb-shadow">
            <Sparkles className="w-5 h-5" strokeWidth={3} />
          </div>
          <div className="font-display font-black text-2xl tracking-tight">ReviseAI</div>
        </div>

        <button
          data-testid="nav-dashboard"
          onClick={() => { navigate("/"); setMobileOpen(false); }}
          className={`nb-border rounded-xl p-3 font-bold flex items-center gap-2 transition-all ${
            location.pathname === "/" ? "bg-[#D4C4FB] dark:bg-[#B794F4] nb-shadow" : "bg-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800"
          }`}
        >
          <LayoutDashboard className="w-5 h-5" strokeWidth={2.5} />
          Dashboard
        </button>

        <div className="label-caps text-zinc-600 dark:text-zinc-400 mt-2">My Subjects</div>
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {subjects.length === 0 && (
            <div className="text-sm text-zinc-500 font-medium">No subjects yet. Create your first one →</div>
          )}
          {subjects.map((s) => {
            const Icon = ICONS[s.icon] || BookOpen;
            const isActive = location.pathname === `/subjects/${s.id}`;
            const color = SUBJECT_COLORS[s.color] || SUBJECT_COLORS.primary;
            return (
              <button
                key={s.id}
                data-testid={`sidebar-subject-${s.id}`}
                onClick={() => { navigate(`/subjects/${s.id}`); setMobileOpen(false); }}
                className={`w-full nb-border rounded-xl p-3 flex items-center gap-3 text-left transition-all ${
                  isActive ? "nb-shadow bg-white dark:bg-zinc-900" : "border-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800"
                }`}
              >
                <div className={`w-8 h-8 rounded-lg nb-border ${color.chip} flex items-center justify-center`}>
                  <Icon className="w-4 h-4 text-black" strokeWidth={2.5} />
                </div>
                <span className="font-bold truncate">{s.name}</span>
              </button>
            );
          })}
        </div>

        {/* User block */}
        <div className="nb-border rounded-2xl p-3 bg-zinc-50 dark:bg-zinc-800">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-10 h-10 rounded-full nb-border object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full nb-border bg-[#FFB38E] flex items-center justify-center font-extrabold">
                {(user?.name || "?")[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate text-sm">{user?.name}</div>
              <div className="flex items-center gap-1 text-xs font-bold">
                <Flame className="w-3 h-3 text-orange-500" />
                <span>{user?.streak || 0}d</span>
                <span className="mx-1 text-zinc-400">•</span>
                <span className="text-[#7c3aed] dark:text-[#B794F4]">{user?.xp || 0} XP</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              data-testid="theme-toggle"
              onClick={toggle}
              className="nb-border rounded-lg flex-1 p-2 bg-white dark:bg-zinc-900 hover:bg-zinc-100 dark:hover:bg-zinc-700 flex items-center justify-center"
              aria-label="toggle theme"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button
              data-testid="logout-btn"
              onClick={goLogout}
              className="nb-border rounded-lg flex-1 p-2 bg-white dark:bg-zinc-900 hover:bg-red-100 dark:hover:bg-red-900 flex items-center justify-center"
              aria-label="logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 nb-border bg-white dark:bg-zinc-900 p-3 flex items-center justify-between">
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMobileOpen((v) => !v)}
          className="nb-button px-3 py-2"
        >
          ☰
        </button>
        <div className="font-display font-black text-xl">ReviseAI</div>
        <div className="w-10" />
      </div>

      {/* Main content */}
      <main className="lg:ml-72 min-h-screen">
        <div className="p-6 sm:p-8 md:p-10">{children}</div>
      </main>
    </div>
  );
}
